import { useQuery } from '@tanstack/react-query';
import { formatEatTime } from '@wodi/domain';
import { LoadingBlock, Notice } from '@wodi/ui';
import { useEffect } from 'react';

import { patientAppointmentsQuery } from '../api/appointments.ts';
import { errorText } from '../stock/common.tsx';

/**
 * At Reception, when checking in from the appointment list: says which appointment this
 * check-in is for, and hands its clinic to the check-in panel (appointments plan §6).
 */
export function AppointmentCheckIn({
  patientId,
  appointmentId,
  onFound,
}: {
  patientId: string;
  appointmentId: string;
  onFound: (appointment: { id: string; servicePointId: string } | null) => void;
}) {
  const list = useQuery(patientAppointmentsQuery(patientId));
  const found = list.data?.items.find((a) => a.id === appointmentId);
  const usable = found?.status === 'booked' ? found : undefined;
  useEffect(() => {
    onFound(
      usable === undefined ? null : { id: usable.id, servicePointId: usable.servicePoint.id },
    );
  }, [usable, onFound]);
  if (list.isPending) return <LoadingBlock label="Loading the appointment" rows={1} />;
  if (list.isError) {
    return (
      <Notice tone="error">
        {errorText(
          list.error,
          "The appointment couldn't be loaded. Check the patient in without it.",
        )}
      </Notice>
    );
  }
  if (usable === undefined) {
    return (
      <Notice tone="info">
        That appointment is no longer booked, so this check-in won&apos;t be linked to it.
      </Notice>
    );
  }
  return (
    <Notice tone="info">
      {`Checking in for the ${formatEatTime(new Date(usable.startsAt))} appointment at ${usable.servicePoint.name}.`}
    </Notice>
  );
}
